import React, { useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Menu,
  X,
  User,
  LogOut,
  Shield,
  Building2,
  UserCircle2,
  ChevronDown,
} from "lucide-react";
import { useAuth } from "@/modules/auth";
import Modal from "@/components/ui/Modal";
import Button from "@/components/ui/Button";
import SidebarItem from "./SidebarItem";
import { getNavigationItems } from "./navigationItems";

interface MobileSidebarProps {
  isMobileOpen: boolean;
  setIsMobileOpen: (open: boolean) => void;
  organizationName?: string | null;
  organizationLogoUrl?: string | null;
}

const MobileSidebar: React.FC<MobileSidebarProps> = ({
  isMobileOpen,
  setIsMobileOpen,
  organizationName,
  organizationLogoUrl,
}) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);

  const isActivePath = (path: string) => location.pathname === path;

  const handleLogout = () => {
    setIsLogoutModalOpen(true);
  };

  const confirmLogout = () => {
    setIsLogoutModalOpen(false);
    setIsUserMenuOpen(false);
    setIsMobileOpen(false);
    logout();
  };

  const userMenuItems = useMemo(() => {
    const items = [
      { label: "Mi Perfil", path: "/profile", icon: <UserCircle2 size={16} /> },
      { label: "Seguridad", path: "/settings/2fa", icon: <Shield size={16} /> },
    ];

    if (user?.role === "ADMIN") {
      items.splice(1, 0, {
        label: "Mi Organización",
        path: "/organization",
        icon: <Building2 size={16} />,
      });
    }

    return items;
  }, [user?.role]);

  return (
    <>
      {/* Mobile menu button */}
      <div className="xl:hidden fixed top-3 left-3 sm:top-4 sm:left-4 z-50">
        <button
          onClick={() => setIsMobileOpen(!isMobileOpen)}
          className="p-2.5 sm:p-3 rounded-xl bg-navy/90 backdrop-blur-sm text-white hover:bg-navy transition-all duration-200 shadow-legal-lg"
        >
          {isMobileOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {/* Mobile backdrop */}
      {isMobileOpen && (
        <div
          className="xl:hidden fixed inset-0 bg-navy/20 backdrop-blur-sm z-40"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <aside
        className={`
                xl:hidden fixed inset-y-0 left-0 z-40 w-[85vw] max-w-72 sidebar-legal transform transition-transform duration-300 ease-in-out
                ${isMobileOpen ? "translate-x-0" : "-translate-x-full"}
            `}
      >
        <div className="flex flex-col h-full safe-bottom">
          {/* Logo */}
          <div className="header-legal-dark flex items-center h-16 flex-shrink-0 px-6">
            <div className="flex items-center gap-3 min-w-0 w-full">
              <div className="w-10 h-10 rounded-lg bg-white/95 flex items-center justify-center shadow-legal overflow-hidden flex-shrink-0">
                {organizationLogoUrl ? (
                  <img
                    src={organizationLogoUrl}
                    alt="Logo de empresa"
                    className="w-full h-full object-contain p-1"
                  />
                ) : (
                  <img
                    src="/LOGO_PLUSSIGNER.svg"
                    alt="Plussigner"
                    className="w-full h-full object-contain p-1"
                  />
                )}
              </div>
              <span
                className="text-white font-medium text-base min-w-0 truncate"
                title={organizationName || "Plussigner"}
              >
                {organizationName || "Plussigner"}
              </span>
            </div>
          </div>

          {/* Mobile Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
            {getNavigationItems(user?.role).map((item) => (
              <SidebarItem
                key={item.href}
                icon={item.icon}
                label={item.name}
                path={item.href}
                isActive={isActivePath(item.href)}
                onClick={() => setIsMobileOpen(false)}
                isCollapsed={false}
              />
            ))}
          </nav>

          {/* Mobile User info */}
          {user && (
            <div className="flex-shrink-0 px-4 py-4 border-t border-white/10">
              <div className="flex items-center gap-3 px-4 py-3 mb-2">
                <button
                  onClick={() => setIsUserMenuOpen((prev) => !prev)}
                  className="w-full flex items-center gap-3"
                >
                  <div className="w-8 h-8 bg-gradient-gold rounded-full flex items-center justify-center">
                    <User size={16} className="text-white" />
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <p className="text-sm font-medium text-white truncate">
                      {user.fullName}
                    </p>
                    <p className="text-xs text-white/70 truncate">
                      {user.profile?.profileName}
                    </p>
                  </div>
                  <ChevronDown
                    size={16}
                    className={`text-white/70 transition-transform ${
                      isUserMenuOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>
              </div>

              {isUserMenuOpen && (
                <div className="mb-3 px-1 space-y-1">
                  {userMenuItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => {
                        setIsMobileOpen(false);
                        setIsUserMenuOpen(false);
                      }}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                        isActivePath(item.path)
                          ? "bg-primary/25 text-white"
                          : "text-white/85 hover:text-white hover:bg-white/10"
                      }`}
                    >
                      {item.icon}
                      <span>{item.label}</span>
                    </Link>
                  ))}
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-red-300 hover:text-red-200 hover:bg-red-500/15 transition-colors"
                  >
                    <LogOut size={16} />
                    <span>Cerrar Sesión</span>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </aside>

      <Modal
        isOpen={isLogoutModalOpen}
        onClose={() => setIsLogoutModalOpen(false)}
        title="Confirmar cierre de sesión"
        footer={
          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => setIsLogoutModalOpen(false)}
            >
              Cancelar
            </Button>
            <Button variant="danger" onClick={confirmLogout}>
              Cerrar sesión
            </Button>
          </div>
        }
      >
        <p className="text-sm text-slate-600">
          ¿Seguro que deseas cerrar sesión? Tendrás que iniciar sesión
          nuevamente para continuar.
        </p>
      </Modal>
    </>
  );
};

export default MobileSidebar;
